import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faTimesCircle,
  faUser,
} from '@fortawesome/pro-solid-svg-icons'
import { faList } from '@fortawesome/pro-solid-svg-icons/faList'
import { RankmiTable } from '@rankmi/rankmi-table'
import { Spin } from 'antd'

import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import {
  COURSE_USER_EVALUATIONS,
  TDataCourseUserEvaluations,
} from 'graphqlAPI/courses/users/queries'
import { AvatarWithInfo } from 'components/reusables/avatarWithInfo/AvatarWithInfo'
import { COLORS } from 'components/reusables/DesignConstants'
import { prefix } from '../../constants'
import styles from './EvaluationsDetailModal.module.scss'

// Tipo para el detalle de evaluación del curso
interface CourseEvaluationDetail {
  id: string
  evaluationName?: string | null
  required?: boolean | null
  minScore?: number | null
  maxScore?: number | null
  responded: boolean
  approved?: boolean | null
  totalScore?: number | null
  canRestart?: boolean
}

interface EvaluationsDetailModalProps {
  isOpen: boolean
  onClose: () => void
  courseId: string
  userId: string
  user: {
    name?: string | null
    lastname?: string | null
    avatar?: string | null
    position?: string | null
  }
}

const prefixModal = `${prefix}.evaluationsDetailModal`

export const EvaluationsDetailModal = ({
  isOpen,
  onClose,
  courseId,
  userId,
  user,
}: EvaluationsDetailModalProps) => {
  const { t } = useTranslation()

  const { data, loading } = useQuery<TDataCourseUserEvaluations>(
    COURSE_USER_EVALUATIONS,
    {
      variables: { courseId, userId: parseInt(userId, 10) },
      skip: !isOpen,
      fetchPolicy: 'network-only',
    }
  )

  const evaluations = useMemo((): CourseEvaluationDetail[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const node = data?.courseUsers?.nodes?.[0] as any
    return (node?.allEvaluations || []) as CourseEvaluationDetail[]
  }, [data])

  const columns = useMemo(
    () => [
      {
        title: t(`${prefixModal}.columns.evaluation`),
        key: 'evaluationName',
        render: (_: unknown, record: CourseEvaluationDetail) => (
          <div className={styles.evaluationName}>
            {record.evaluationName || '-'}
          </div>
        ),
      },
      {
        title: t(`${prefixModal}.columns.required`),
        key: 'required',
        width: 150,
        render: (_: unknown, record: CourseEvaluationDetail) => (
          <span>
            {record.required
              ? t(`${prefixModal}.required.mandatory`)
              : t(`${prefixModal}.required.optional`)}
          </span>
        ),
      },
      {
        title: t(`${prefixModal}.columns.score`),
        key: 'score',
        width: 88,
        render: (_: unknown, record: CourseEvaluationDetail) => {
          // Si no respondió, mostrar "No realizada"
          if (!record.responded) {
            return (
              <div className={styles.notRespondedChip}>
                {t(`${prefixModal}.notResponded`)}
              </div>
            )
          }

          // Si approved es null, significa que la evaluación fue reiniciada
          // y aún no tiene un resultado definitivo, mostrar como "No realizada"
          if (record.approved === null) {
            return (
              <div className={styles.notRespondedChip}>
                {t(`${prefixModal}.notResponded`)}
              </div>
            )
          }

          const isApproved = record.approved === true
          return (
            <div
              className={styles.scoreChip}
              style={{
                backgroundColor: isApproved ? '#D7F5E9' : '#FDE0E0',
                borderColor: isApproved ? '#88E3BC' : '#F7A2A2',
              }}
            >
              <FontAwesomeIcon
                icon={isApproved ? faCheckCircle : faTimesCircle}
                color={isApproved ? '#00B47C' : '#F5222D'}
                className="mr-1"
              />
              <span style={{ color: isApproved ? '#00B47C' : '#F5222D' }}>
                {record.totalScore}/{record.maxScore}{' '}
                {t(`${prefixModal}.points`)}
              </span>
            </div>
          )
        },
      },
    ],
    [t]
  )

  const dataSource = useMemo(
    () =>
      evaluations.map((evaluation) => ({
        ...evaluation,
        key: evaluation.id,
      })),
    [evaluations]
  )

  const locale = {
    header: {
      rowsCount: t(`${prefixModal}.rowsCount`),
      rowsCount_plural: t(`${prefixModal}.rowsCount_plural`),
    },
  }

  return (
    <Modal
      title={t(`${prefixModal}.title`)}
      close={onClose}
      isOpen={isOpen}
      maxwidth={700}
      maxheight={500}
      actions={
        <ModalButtons
          secondaryText={t(`${prefixModal}.close`)}
          secondaryAction={onClose}
        />
      }
    >
      <div className={styles.container}>
        <div className={styles.userHeader}>
          <AvatarWithInfo
            size={40}
            borderRadius={16}
            image={user.avatar}
            icon={faUser}
            background={COLORS.lightBlue}
            title={`${user.name} ${user.lastname}`}
            subtitle={user.position || ''}
          />
        </div>
        <div className={styles.tableContainer}>
          {loading ? (
            <div className={styles.loading}>
              <Spin />
            </div>
          ) : (
            <RankmiTable
              columnHeaders={columns}
              data={dataSource}
              total={evaluations.length}
              locale={locale}
              countIcon={faList}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}
